import Checkbox from "@mui/material/Checkbox";
import _ from "lodash";
import { observer } from "mobx-react-lite";
import { ReactElement, useEffect, useMemo, useState } from "react";
import { CSVLink } from 'react-csv';
import { Link } from "react-router-dom";
import commonStyles from "./Common.module.css";
import styles from "./Grid.module.css";
import DownloadIcon from "./icons/DownloadIcon";

function Grid<T>({ data, headers, downloadHeaders, getId, downloadFileName, header, controls, onRowClick, isRowActive, ...props }: IGridProps<T>) {
    const [sortHeaderKey, setSortHeaderKey] = useState<string | null>(null);
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
    const sortedData = useMemo(() => {
        const header = headers.find(h => getHeaderKey(h) === sortHeaderKey);
        if (!header)
            return data;

        return _.orderBy(data, header.getValue, sortOrder);
    }, [data, headers, sortHeaderKey, sortOrder]);

    const [selectedItems, setSelectedItems] = useState<T[]>([]);
    const selectedItemsData = useMemo(() => {
        const items = selectedItems.length ? selectedItems : data;
        const csvHeaders = downloadHeaders ?? headers;

        return [
            csvHeaders.map(header => header.titleStrings.join(' ')),
            ...items.map(item => csvHeaders.map(header => {
                return header.getStringLabel
                    ? header.getStringLabel(item)
                    : header.getLabel(item) as string;
            })),
        ];
    }, [data, downloadHeaders, headers, selectedItems]);

    const setSort = (headerKey: string) => {
        if (headerKey === sortHeaderKey)
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        else {
            setSortHeaderKey(headerKey);
            setSortOrder("asc");
        }
    };

    const toggleItem = (item: T) => {
        !selectedItems.includes(item)
            ? selectItems([
                ...selectedItems,
                item,
            ])
            : selectItems(selectedItems.filter(si => si !== item))
    }

    const selectItems = (data: T[]) => {
        setSelectedItems(data);
        props.setSelectedItems && props.setSelectedItems(data);
    }

    const onCellClick = (header: IHeader<T>, item: T, e: React.MouseEvent<any, MouseEvent>) => {
        if (header.onClick) {
            e.stopPropagation();
            header.onClick(item);
        }
    }

    useEffect(() => setSelectedItems([]), [data]);

    return (
        <>
            <div className={styles.header}>
                {header}
                <div className={styles.controls}>
                    {controls}
                    <CSVLink data={selectedItemsData} filename={downloadFileName}>
                        <button className={styles.control}>
                            <DownloadIcon color="black" /> Download
                        </button>
                    </CSVLink>
                </div>
            </div>
            <table className={styles.grid}>
                <thead>
                    <tr>
                        <th>
                            <Checkbox
                                disabled={!data.length}
                                checked={!!data.length && data.every(i => selectedItems.includes(i))}
                                onChange={e => e.target.checked ? selectItems([...data]) : selectItems([])}
                            />
                        </th>
                        {headers.map(h => (
                            <th
                                key={getHeaderKey(h)}
                                className={[h.isFullWidth && styles.fullWidthColumn, getAlignClassName(h.align || "center")].join(" ")}
                                onClick={() => setSort(getHeaderKey(h))}
                            >
                                {h.titleStrings.map(ts => <div key={ts}>{ts}</div>)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {!sortedData.length && <tr><td colSpan={headers.length + 1} className={styles.alignCenter}>There is no data to display</td></tr>}
                    {sortedData.map(item => (
                        <tr key={getId(item)} className={isRowActive && isRowActive(item) ? styles.activeRow : styles.row} onClick={onRowClick && (() => onRowClick(item))}>
                            <td className={styles.alignCenter}>
                                <Checkbox checked={selectedItems.includes(item)} onChange={() => toggleItem(item)} onClick={e => e.stopPropagation()} />
                            </td>
                            {headers.map(h => (
                                <td key={getHeaderKey(h)} className={getAlignClassName(h.align || "center")}>
                                    {h.getLinkAddress ? (
                                        <Link to={h.getLinkAddress(item)} className={commonStyles.link} onClick={e => onCellClick(h, item, e)}>{h.getLabel(item)}</Link>
                                    ) : (
                                        <span className={h.onClick ? commonStyles.link : ""} onClick={e => onCellClick(h, item, e)}>{h.getLabel(item)}</span>
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
}

const getHeaderKey = <T,>(header: IHeader<T>) => header.titleStrings.join(" ");

const getAlignClassName = (align: "left" | "center" | "right") => {
    switch (align) {
        case "left": return styles.alignLeft;
        case "center": return styles.alignCenter;
        case "right": return styles.alignRight;
    }
};

export default observer(Grid);

interface IGridProps<T> {
    data: T[];
    headers: IHeader<T>[];
    downloadHeaders?: IHeader<T>[];
    getId: (item: T) => any;
    downloadFileName: string;
    setSelectedItems?: (items: T[]) => void;
    header: React.ReactNode;
    controls?: React.ReactNode[];
    onRowClick?: (item: T) => void;
    isRowActive?: (item: T) => boolean;
}

export interface IHeader<T> {
    titleStrings: string[];
    align?: "left" | "center" | "right";
    isFullWidth?: boolean;
    getValue: (item: T) => any;
    getLabel: (item: T) => ReactElement | string;
    getStringLabel?: (item: T) => string;
    getLinkAddress?: (item: T) => string;
    onClick?: (item: T) => void;
}
